import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileAudio, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function History() {
  const [runs, setRuns] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();
  const loadRuns = () => api.get("/runs").then(({ data }) => setRuns(data)).catch(() => {});
  useEffect(() => { loadRuns(); }, []);

  const canDelete = (r) => user && (user.role === "admin" || user.email === r.user_email);
  const del = async (e, rid) => {
    e.stopPropagation();
    try { await api.delete(`/runs/${rid}`); toast.success("Run deleted."); setConfirmId(null); loadRuns(); }
    catch (err) { toast.error(err?.response?.status === 403 ? "Not allowed." : "Delete failed."); }
  };

  const cols = ["run", "direction", "status", "dur", "cpm", "wasted", "cost", ""];

  return (
    <div>
      <div className="overline mb-2">all runs</div>
      <h1 className="font-mono font-extrabold tracking-tighter text-3xl mb-8">Run history</h1>
      <div className="border border-border overflow-x-auto" data-testid="history-table">
        <table className="w-full font-mono text-sm">
          <thead><tr className="border-b border-border bg-[#18181B]">
            {cols.map((c, i) => <th key={c || i} className={`p-3 overline ${["dur", "cpm", "wasted", "cost"].includes(c) ? "text-right" : "text-left"}`} style={{ fontSize: "0.55rem" }}>{c}</th>)}
          </tr></thead>
          <tbody>
            {runs.length === 0 && <tr><td colSpan={8} className="p-6 text-muted-foreground bg-[#18181B]">No runs yet.</td></tr>}
            {runs.map((r) => (
              <tr key={r.id} data-testid={`history-row-${r.id}`} onClick={() => nav(`/runs/${r.id}`)}
                className="border-b border-border last:border-b-0 bg-[#18181B] hover:bg-secondary cursor-pointer transition-colors">
                <td className="p-3 flex items-center gap-2"><FileAudio size={15} className="text-muted-foreground" /><span className="truncate max-w-[220px]">{r.original_filename}</span></td>
                <td className="p-3 text-muted-foreground">{r.direction}</td>
                <td className="p-3" style={{ color: r.status === "completed" ? "#00E559" : (r.status === "failed" || r.status === "interrupted") ? "#FF3333" : "#FF5500" }}>{r.status}</td>
                <td className="p-3 text-right text-muted-foreground">{r.duration_s != null ? `${r.duration_s}s` : "—"}</td>
                <td className="p-3 text-right" style={{ color: r.cost_per_min == null ? "#52525b" : r.cost_per_min < 0.15 ? "#00E559" : "#FF3333" }}>{r.cost_per_min != null ? `$${r.cost_per_min}` : "—"}</td>
                <td className="p-3 text-right" style={{ color: r.wasted_compute_pct == null ? "#52525b" : r.wasted_compute_pct < 15 ? "#00E559" : "#FF3333" }}>{r.wasted_compute_pct != null ? `${r.wasted_compute_pct}%` : "—"}</td>
                <td className="p-3 text-right text-muted-foreground">{r.total_cost != null ? `$${r.total_cost}` : "—"}</td>
                <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                  {canDelete(r) && (confirmId === r.id ? (
                    <span className="flex items-center justify-end gap-2">
                      <button data-testid={`confirm-delete-${r.id}`} onClick={(e) => del(e, r.id)} className="text-[#FF3333] hover:text-[#ff6666]">confirm</button>
                      <button data-testid={`cancel-delete-${r.id}`} onClick={(e) => { e.stopPropagation(); setConfirmId(null); }} className="text-muted-foreground hover:text-foreground">cancel</button>
                    </span>
                  ) : (
                    <button data-testid={`delete-row-${r.id}`} onClick={(e) => { e.stopPropagation(); setConfirmId(r.id); }} className="text-muted-foreground hover:text-[#FF3333] transition-colors"><Trash size={15} /></button>
                  ))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
